import { useState, useEffect } from 'react';
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride';
import { Button } from '@/components/ui/button';
import { GraduationCap } from 'lucide-react';

interface ComplianceWalkthroughProps {
  onComplete?: () => void;
}

const ComplianceWalkthrough = ({ onComplete }: ComplianceWalkthroughProps) => {
  const [run, setRun] = useState(false);
  const [hasCompletedWalkthrough, setHasCompletedWalkthrough] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('compliance_walkthrough_completed');
    setHasCompletedWalkthrough(!!completed);
  }, []);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Welcome to Compliance Review!</h3>
          <p>Let's walk through two common scenarios:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>✅ Approving a compliant local coffee shop deal</li>
            <li>❌ Rejecting an alcohol brand deal (NCAA violation)</li>
          </ul>
          <p className="text-sm text-muted-foreground">This will take about 2 minutes.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-deal-type="alcohol"]',
      content: (
        <div className="space-y-2">
          <h3 className="font-semibold">❌ Flagged: Alcohol Brand Deal</h3>
          <p className="text-sm">This deal is automatically flagged because alcohol brands violate NCAA policy.</p>
          <p className="text-sm text-muted-foreground">Notice the red warning badge and compliance issues listed.</p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-deal-type="alcohol"] [data-action="reject"]',
      content: (
        <div className="space-y-2">
          <h3 className="font-semibold">Reject This Deal</h3>
          <p className="text-sm">Click the <strong>Reject</strong> button to deny this non-compliant deal.</p>
          <p className="text-sm text-muted-foreground">You'll be asked to provide a reason - mention the NCAA alcohol policy violation.</p>
        </div>
      ),
      placement: 'left',
      spotlightClicks: true,
    },
    {
      target: '[data-deal-type="coffee"]',
      content: (
        <div className="space-y-2">
          <h3 className="font-semibold">✅ Clean: Coffee Shop Deal</h3>
          <p className="text-sm">This local business partnership is compliant - low risk, clear terms, no conflicts.</p>
          <p className="text-sm text-muted-foreground">Notice the green status and low risk score.</p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-deal-type="coffee"] [data-action="approve"]',
      content: (
        <div className="space-y-2">
          <h3 className="font-semibold">Approve This Deal</h3>
          <p className="text-sm">Click the <strong>Approve</strong> button to greenlight this compliant deal.</p>
          <p className="text-sm text-muted-foreground">The athlete will be notified and the deal moves to active status.</p>
        </div>
      ),
      placement: 'left',
      spotlightClicks: true,
    },
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">🎉 You're Ready!</h3>
          <p>You now know how to:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Identify compliance issues</li>
            <li>Reject non-compliant deals</li>
            <li>Approve clean deals</li>
          </ul>
          <p className="text-sm text-muted-foreground">Try reviewing the other pending deals to practice!</p>
        </div>
      ),
      placement: 'center',
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem('compliance_walkthrough_completed', 'true');
      setHasCompletedWalkthrough(true);
      onComplete?.();
    }
  };

  const startWalkthrough = () => {
    setRun(true);
  };

  const resetWalkthrough = () => {
    localStorage.removeItem('compliance_walkthrough_completed');
    setHasCompletedWalkthrough(false);
    setRun(true);
  };

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: 'hsl(var(--primary))',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: '8px',
          },
          buttonNext: {
            backgroundColor: 'hsl(var(--primary))',
            borderRadius: '6px',
          },
          buttonBack: {
            color: 'hsl(var(--muted-foreground))',
          },
          buttonSkip: {
            color: 'hsl(var(--muted-foreground))',
          },
        }}
      />

      {!hasCompletedWalkthrough ? (
        <Button
          onClick={startWalkthrough}
          className="gap-2"
          variant="outline"
        >
          <GraduationCap className="h-4 w-4" />
          Start Compliance Tutorial
        </Button>
      ) : (
        <Button
          onClick={resetWalkthrough}
          variant="ghost"
          size="sm"
          className="gap-2"
        >
          <GraduationCap className="h-4 w-4" />
          Replay Tutorial
        </Button>
      )}
    </>
  );
};

export default ComplianceWalkthrough;
